import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "./Firebase";

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const cv = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));

const DocLink = ({ url }) => {
    if (!url) return <span className="text-muted">-</span>;
    return (
        <a href={url} target="_blank" rel="noreferrer"
            style={{ fontSize: "11px", color: "#2563eb", wordBreak: "break-all", whiteSpace: "normal", display: "block", lineHeight: "1.4" }}>
            {url}
        </a>
    );
};

const STATUS_COLORS = {
    disbursed: "bg-success", approved: "bg-primary",
    rejected: "bg-danger", pending: "bg-warning text-dark",
};
const statusBadge = (s) => {
    const cls = STATUS_COLORS[(s || "").toLowerCase()] || "bg-secondary";
    return <span className={`badge ${cls}`} style={{ fontSize: "11px", padding: "4px 8px" }}>{cv(s)}</span>;
};
const statusFromInt = (val) => {
    const n = Number(val);
    if (n === 1) return statusBadge("Disbursed");
    return statusBadge("Pending");
};

const PROGRESS_STEPS = [
    "Document Verification",
    "Video Verification",
    "Physical Verification",
    "Penny Verification",
    "Loan Disbursement",
];

const getStep = (progress, label) =>
    (progress || []).find(p => (p.label || "").toLowerCase() === label.toLowerCase()) || null;

const EMI_PLANS = {
    1: "₹4000 For 5 Months",
    2: "₹5000 For 4 Months",
    3: "₹1000 For 10 Months",
    4: "₹2500 For 4 Months",
    5: "₹1250 For 8 Months",
    6: "₹3750 For 4 Months",
    7: "₹3000 For 5 Months",
    8: "₹5000 For 5 Months",
    9: "₹3125 For 8 Months",
};
const emiLabel = (val) => EMI_PLANS[Number(val)] || cv(val);

const DOC_FIELDS = [
    ["Aadhar_Front", "Aadhar F"],
    ["Aadhar_Back", "Aadhar B"],
    ["Pan_Card", "PAN"],
    ["Passbook_Front", "Passbook"],
    ["Bank_Statement", "Bank Stmt"],
    ["Passportsize_Photo", "Photo"],
    ["Building_Front_Image", "Bldg Front"],
    ["Building_Interior_Image", "Bldg Interior"],
    ["Business_Video", "Video"],
    ["Product_Service_Image", "Product Img"],
];

export default function LoanData() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [payModal, setPayModal] = useState(null);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, "nv_loan"));
                const data = snap.docs.map(d => ({ _id: d.id, ...d.data() }));
                data.sort((a, b) => (parseDate(b.created_date)?.getTime() || 0) - (parseDate(a.created_date)?.getTime() || 0));
                setRows(data);
            } catch (err) {
                console.error("LoanData fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const statuses = useMemo(() => {
        const s = new Set(rows.map(r => cv(r.final_status)).filter(v => v !== "-"));
        return ["all", ...Array.from(s).sort()];
    }, [rows]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter(r => {
            const matchSearch = !q ||
                (r.loan_id || "").toLowerCase().includes(q) ||
                (r.fullName || "").toLowerCase().includes(q) ||
                (r.username || "").includes(q) ||
                (r.uipId || "").includes(q) ||
                (r.alternate_phone_no || "").includes(q) ||
                (r.businessName || "").toLowerCase().includes(q) ||
                (r.city || "").toLowerCase().includes(q);
            const matchStatus = statusFilter === "all" ||
                (r.final_status || "").toLowerCase() === statusFilter.toLowerCase();
            return matchSearch && matchStatus;
        });
    }, [rows, search, statusFilter]);

    const handleExport = () => {
        const mainData = filtered.map((r) => {
            const row = {};
            const ps = (label) => getStep(r.progress, label);

            row["Loan ID"] = cv(r.loan_id);
            row["Phone"] = cv(r.username || r.uipId);
            row["Full Name"] = cv(r.fullName);
            row["Created"] = fmtDateTime(r.created_date);
            row["Loan Amount"] = cv(r.loanAmount);
            row["Purpose + Specify"] = [cv(r.whyNeedGrant), cv(r.nameOfEqupiment)].filter(v => v !== "-").join(" / ");
            row["Select EMI Plan"] = emiLabel(r.selected_emi_plan);
            row["Have you taken any loan in the past?"] = cv(r.takenPastLoan);
            row["How did you get to know about this loan?"] = cv(r.aboutThisLoan);
            row["Date of Birth"] = cv(r.dob);
            row["Marital Status"] = cv(r.maritalStatus);
            row["Gender"] = cv(r.gender);
            row["Father's Name"] = cv(r.fatherName);
            row["Mother's Name"] = cv(r.motherName);
            row["State"] = cv(r.state);
            // row["not captured currently"]                    = "-";
            row["Pincode"] = cv(r.pincode);
            row["Full Address"] = cv(r.address);
            row["Highest Education"] = cv(r.education);
            row["Business Type (Primary Occupation)"] = cv(r.businessType);
            row["Business Name"] = cv(r.businessName);
            row["Secondary Occupation"] = cv(r.secondary_occupation);
            row["Working Since"] = cv(r.businessEstablishedYear);
            row["Monthly Income"] = cv(r.monthlyIncome);
            row["Monthly Household Income"] = cv(r.alternateIncome);
            row["Account No."] = cv(r.bankAccountNumber);
            row["Account Holder"] = cv(r.fullName);
            row["Name of Bank"] = cv(r.bankName);
            row["IFSC"] = cv(r.ifscCode);
            row["UPI ID"] = "-";
            row["PAN Card"] = cv(r.Pan_Card);
            row["PAN No. (auto-fetch)"] = cv(r.pan_number);
            row["Aadhaar Card"] = cv(r.Aadhar_Front);
            row["Aadhaar No. (auto-fetch)"] = cv(r.aadhar_number);
            row["Document Verification: Date"] = cv(ps("Document Verification")?.date);
            row["Document Verification: Remarks"] = cv(ps("Document Verification")?.message);
            row["Document Verification: Status"] = cv(ps("Document Verification")?.status);
            row["Video Verification: Date"] = cv(ps("Video Verification")?.date);
            row["Video Verification: Remarks"] = cv(ps("Video Verification")?.message);
            row["Video Verification: Status"] = cv(ps("Video Verification")?.status);
            row["Physical Verification: Date"] = cv(ps("Physical Verification")?.date);
            row["Physical Verification: Remarks"] = cv(ps("Physical Verification")?.message);
            row["Physical Verification: Status"] = cv(ps("Physical Verification")?.status);
            row["Loan Disbursement: Date"] = cv(ps("Loan Disbursement")?.date);
            row["Loan Disbursement: Remarks"] = cv(ps("Loan Disbursement")?.message);
            row["Loan Disbursement: Status"] = cv(ps("Loan Disbursement")?.status);
            row["Status"] = Number(r.status) === 1 ? "Disbursed" : "Pending";

            return row;
        });

        const payData = [];
        filtered.forEach(r => {
            (r.payment_history || []).forEach((p, i) => {
                payData.push({
                    "Loan ID": cv(r.loan_id), "Full Name": cv(r.fullName),
                    "EMI #": p.emi ?? i + 1, "Amount": p.amount ?? "-",
                    "Paid Amount": p.paid_amount ?? "-", "Due Date": cv(p.dueDate),
                    "Paid Date": cv(p.paidDate), "Status": cv(p.status),
                    "Is Success": p.isSuccess ? "Yes" : "No",
                });
            });
        });

        const progressData = [];
        filtered.forEach(r => {
            (r.progress || []).forEach((p, i) => {
                progressData.push({
                    "Loan ID": cv(r.loan_id), "Full Name": cv(r.fullName),
                    "Step #": i + 1, "Label": cv(p.label), "Status": cv(p.status),
                    "Is Complete": p.isComplete ? "Yes" : "No", "Date": cv(p.date), "Message": cv(p.message),
                });
            });
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(mainData), "Loan Data");
        if (payData.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(payData), "Payment History");
        if (progressData.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(progressData), "Progress");
        XLSX.writeFile(wb, "nv_loan_data.xlsx");
    };

    const paidCount = (r) => (r.payment_history || []).filter(p => p.isSuccess).length;
    const totalEmi = (r) => (r.payment_history || []).length;

    return (
        <>
            <div className="container-fluid py-4">

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h3 className="mb-1">Loan Data</h3>
                        <div className="text-muted" style={{ fontSize: "13px" }}>nv_loan · {rows.length} total records</div>
                    </div>
                    <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
                </div>

                {/* SEARCH + FILTER + EXPORT */}
                <div className="card mb-3">
                    <div className="card-body py-3 px-4">
                        <div className="d-flex gap-3 align-items-center flex-wrap">
                            {/* <input
                                type="text"
                                className="form-control"
                                placeholder="Search by loan ID, name, phone, business, city…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ maxWidth: "320px" }}
                            />
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                                style={{ maxWidth: "180px" }}
                            >
                                {statuses.map(s => (
                                    <option key={s} value={s}>{s === "all" ? "All Statuses" : s}</option>
                                ))}
                            </select>
                            {(search || statusFilter !== "all") && (
                                <span className="text-muted" style={{ fontSize: "13px" }}>
                                    {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                                </span>
                            )} */}
                            <button
                                className="btn btn-success ms-auto"
                                onClick={handleExport}
                                disabled={loading || filtered.length === 0}
                                style={{ minWidth: "140px", fontWeight: 700 }}
                            >
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                        <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                    </div>
                ) : (
                    <div className="card">
                        <div className="table-responsive" style={{ overflowX: "auto" }}>
                            <table className="table table-bordered table-hover align-middle mb-0"
                                style={{ fontSize: "12px", minWidth: "5200px", whiteSpace: "nowrap" }}>
                                <thead className="table-dark" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                                    <tr>
                                        <th style={{ minWidth: "40px" }}>#</th>
                                        <th style={{ minWidth: "160px" }}>Loan ID</th>
                                        <th style={{ minWidth: "110px" }}>Phone</th>
                                        <th style={{ minWidth: "130px" }}>Full Name</th>
                                        <th style={{ minWidth: "130px" }}>Created</th>
                                        <th style={{ minWidth: "100px" }}>Loan Amount</th>
                                        <th style={{ minWidth: "180px" }}>Purpose + Specify</th>
                                        <th style={{ minWidth: "100px" }}>Select EMI Plan</th>
                                        <th style={{ minWidth: "140px" }}>Have you taken any loan in the past?</th>
                                        <th style={{ minWidth: "180px" }}>How did you get to know about this loan?</th>
                                        <th style={{ minWidth: "100px" }}>Date of Birth</th>
                                        <th style={{ minWidth: "100px" }}>Marital Status</th>
                                        <th style={{ minWidth: "70px" }}>Gender</th>
                                        <th style={{ minWidth: "120px" }}>Father's Name</th>
                                        <th style={{ minWidth: "120px" }}>Mother's Name</th>
                                        <th style={{ minWidth: "110px" }}>State</th>
                                        {/* <th style={{ minWidth: "120px" }}>not captured currently</th> */}
                                        <th style={{ minWidth: "80px" }}>Pincode</th>
                                        <th style={{ minWidth: "220px" }}>Full Address</th>
                                        <th style={{ minWidth: "110px" }}>Highest Education</th>
                                        <th style={{ minWidth: "180px" }}>Business Type (Primary Occupation)</th>
                                        <th style={{ minWidth: "160px" }}>Business Name</th>
                                        <th style={{ minWidth: "130px" }}>Secondary Occupation</th>
                                        <th style={{ minWidth: "100px" }}>Working Since</th>
                                        <th style={{ minWidth: "110px" }}>Monthly Income</th>
                                        <th style={{ minWidth: "140px" }}>Monthly Household Income</th>
                                        <th style={{ minWidth: "140px" }}>Account No.</th>
                                        <th style={{ minWidth: "130px" }}>Account Holder</th>
                                        <th style={{ minWidth: "130px" }}>Name of Bank</th>
                                        <th style={{ minWidth: "100px" }}>IFSC</th>
                                        <th style={{ minWidth: "80px" }}>UPI ID</th>
                                        <th style={{ minWidth: "200px" }}>PAN Card</th>
                                        <th style={{ minWidth: "120px" }}>PAN No. (auto-fetch)</th>
                                        <th style={{ minWidth: "200px" }}>Aadhaar Card</th>
                                        <th style={{ minWidth: "140px" }}>Aadhaar No. (auto-fetch)</th>
                                        <th style={{ minWidth: "110px" }}>Document Verification: Date</th>
                                        <th style={{ minWidth: "150px" }}>Document Verification: Remarks</th>
                                        <th style={{ minWidth: "120px" }}>Document Verification: Status</th>
                                        <th style={{ minWidth: "110px" }}>Video Verification: Date</th>
                                        <th style={{ minWidth: "150px" }}>Video Verification: Remarks</th>
                                        <th style={{ minWidth: "120px" }}>Video Verification: Status</th>
                                        <th style={{ minWidth: "110px" }}>Physical Verification: Date</th>
                                        <th style={{ minWidth: "150px" }}>Physical Verification: Remarks</th>
                                        <th style={{ minWidth: "120px" }}>Physical Verification: Status</th>
                                        <th style={{ minWidth: "110px" }}>Loan Disbursement: Date</th>
                                        <th style={{ minWidth: "150px" }}>Loan Disbursement: Remarks</th>
                                        <th style={{ minWidth: "120px" }}>Loan Disbursement: Status</th>
                                        <th style={{ minWidth: "100px" }}>Status</th>
                                        {/* <th style={{ minWidth: "100px" }}>EMI History</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan="50" className="text-center py-4 text-muted">
                                                {search || statusFilter !== "all" ? "No results found" : "No loan data found"}
                                            </td>
                                        </tr>
                                    ) : filtered.map((r, i) => {
                                        const ps = (label) => getStep(r.progress, label);
                                        const progCell = (label) => {
                                            const s = ps(label);
                                            return s ? (
                                                <span className={`badge ${s.isComplete ? "bg-success" : "bg-secondary"}`}
                                                    style={{ fontSize: "11px", padding: "3px 7px" }}>
                                                    {cv(s.status)}
                                                </span>
                                            ) : "-";
                                        };
                                        return (
                                            <tr key={r._id}>
                                                <td>{i + 1}</td>
                                                <td style={{ fontWeight: 600 }}>{cv(r.loan_id)}</td>
                                                <td>{cv(r.username || r.uipId)}</td>
                                                <td style={{ fontWeight: 600 }}>{cv(r.fullName)}</td>
                                                <td>{fmtDateTime(r.created_date)}</td>
                                                <td style={{ fontWeight: 700, color: "#16a34a" }}>{r.loanAmount ? `₹${r.loanAmount}` : "-"}</td>
                                                <td style={{ whiteSpace: "normal", maxWidth: "220px" }}>
                                                    {[cv(r.whyNeedGrant), cv(r.nameOfEqupiment)].filter(v => v !== "-").join(" / ")}
                                                </td>
                                                <td>{emiLabel(r.selected_emi_plan)}</td>
                                                <td>{cv(r.takenPastLoan)}</td>
                                                <td>{cv(r.aboutThisLoan)}</td>
                                                <td>{cv(r.dob)}</td>
                                                <td>{cv(r.maritalStatus)}</td>
                                                <td>{cv(r.gender)}</td>
                                                <td>{cv(r.fatherName)}</td>
                                                <td>{cv(r.motherName)}</td>
                                                <td>{cv(r.state)}</td>
                                                {/* <td>-</td> */}
                                                <td>{cv(r.pincode)}</td>
                                                <td style={{ whiteSpace: "normal", minWidth: "220px", maxWidth: "280px" }}>{cv(r.address)}</td>
                                                <td>{cv(r.education)}</td>
                                                <td>{cv(r.businessType)}</td>
                                                <td>{cv(r.businessName)}</td>
                                                <td>{cv(r.secondary_occupation)}</td>
                                                <td>{cv(r.businessEstablishedYear)}</td>
                                                <td>{r.monthlyIncome ? `₹${r.monthlyIncome}` : "-"}</td>
                                                <td>{r.alternateIncome ? `₹${r.alternateIncome}` : "-"}</td>
                                                <td>{cv(r.bankAccountNumber)}</td>
                                                <td>{cv(r.fullName)}</td>
                                                <td>{cv(r.bankName)}</td>
                                                <td>{cv(r.ifscCode)}</td>
                                                <td>-</td>
                                                <td style={{ whiteSpace: "normal", minWidth: "200px", maxWidth: "240px", verticalAlign: "top" }}><DocLink url={r.Pan_Card} /></td>
                                                <td>{cv(r.pan_number)}</td>
                                                <td style={{ whiteSpace: "normal", minWidth: "200px", maxWidth: "240px", verticalAlign: "top" }}><DocLink url={r.Aadhar_Front} /></td>
                                                <td>{cv(r.aadhar_number)}</td>
                                                <td>{cv(ps("Document Verification")?.date)}</td>
                                                <td style={{ whiteSpace: "normal", maxWidth: "180px" }}>{cv(ps("Document Verification")?.message)}</td>
                                                <td className="text-center">{progCell("Document Verification")}</td>
                                                <td>{cv(ps("Video Verification")?.date)}</td>
                                                <td style={{ whiteSpace: "normal", maxWidth: "180px" }}>{cv(ps("Video Verification")?.message)}</td>
                                                <td className="text-center">{progCell("Video Verification")}</td>
                                                <td>{cv(ps("Physical Verification")?.date)}</td>
                                                <td style={{ whiteSpace: "normal", maxWidth: "180px" }}>{cv(ps("Physical Verification")?.message)}</td>
                                                <td className="text-center">{progCell("Physical Verification")}</td>
                                                <td>{cv(ps("Loan Disbursement")?.date)}</td>
                                                <td style={{ whiteSpace: "normal", maxWidth: "180px" }}>{cv(ps("Loan Disbursement")?.message)}</td>
                                                <td className="text-center">{progCell("Loan Disbursement")}</td>
                                                <td>{statusFromInt(r.status)}</td>
                                                {/* <td className="text-center">
                                                    {totalEmi(r) > 0 ? (
                                                        <span
                                                            className={`badge ${paidCount(r) === totalEmi(r) ? "bg-success" : "bg-primary"}`}
                                                            style={{ cursor: "pointer", fontSize: "11px", padding: "4px 8px" }}
                                                            onClick={() => setPayModal(r)}
                                                            title="Click to view EMI history"
                                                        >
                                                            {paidCount(r)}/{totalEmi(r)} paid
                                                        </span>
                                                    ) : "-"}
                                                </td> */}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* PAYMENT HISTORY MODAL */}
            {payModal && (
                <div onClick={() => setPayModal(null)}
                    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ background: "#fff", borderRadius: "10px", width: "100%", maxWidth: "700px", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}>
                        <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                            <div>
                                <div className="fw-bold" style={{ fontSize: "15px" }}>Payment History — {cv(payModal.fullName)}</div>
                                <div className="text-muted" style={{ fontSize: "12px" }}>Loan ID: {cv(payModal.loan_id)}</div>
                            </div>
                            <button className="btn-close" onClick={() => setPayModal(null)} />
                        </div>
                        <div style={{ overflowY: "auto", padding: "16px" }}>
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr><th>EMI #</th><th className="text-end">Amount</th><th className="text-end">Paid</th><th>Due Date</th><th>Paid Date</th><th className="text-center">Status</th></tr>
                                </thead>
                                <tbody>
                                    {(payModal.payment_history || []).map((p, i) => (
                                        <tr key={i}>
                                            <td>{p.emi ?? i + 1}</td>
                                            <td className="text-end">₹{p.amount ?? "-"}</td>
                                            <td className="text-end fw-bold" style={{ color: "#16a34a" }}>₹{p.paid_amount ?? "-"}</td>
                                            <td>{cv(p.dueDate)}</td>
                                            <td>{cv(p.paidDate)}</td>
                                            <td className="text-center">
                                                <span className={`badge ${p.isSuccess ? "bg-success" : "bg-secondary"}`} style={{ fontSize: "11px" }}>{cv(p.status)}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
}
